import Config from '@shared/config/constants/config-main';
import axios from 'axios';

import {
  updatePartiallyDCBState,
  updatePartiallyHomeData,
} from '@entities/dcb';
import { useAtomValue } from 'jotai';
import { dcbAtom, languageAtom } from '@shared/store/slice-atoms';
import { useEffect, useRef } from 'react';

const buildHomeUrlForLocale = (locale, country, source) =>
  `${Config.API_URL}/home/${locale}/${country}?source=${source}`;

export const useRefetchHomeData = () => {
  const language = useAtomValue(languageAtom);
  const dcb = useAtomValue(dcbAtom);
  const { homeData, source, dataRetrievedLanguage } = dcb;

  const lastFetchedLangRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);

  const updateHomeDataAndOperatorDetails = async (requestUrl, requestId) => {
    try {
      const dataTC = await axios.get(requestUrl);
      // Ignore out-of-order responses (fast lang toggling).
      if (requestId !== requestIdRef.current) return;

      const payload = dataTC?.data?.data;
      if (!payload) return;

      updatePartiallyHomeData({
        fullTCText: payload.termsText || '',
        ...(payload.operators ? { operators: payload.operators } : {}),
      });
      updatePartiallyDCBState({ dataRetrievedLanguage: payload.language || language.locale });

      const operatorSelectedDetails = payload.operators?.find((el) => el.name === 'Etisalat');
      if (operatorSelectedDetails) {
        updatePartiallyDCBState({ operatorSelectedDetails });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('useRefetchHomeData error', err);
    }
  };

  useEffect(() => {
    const locale = language?.locale;
    const country = homeData?.country;

    if (!locale || !country) return;

    // First run: the terms already match the loaded language, just record it.
    if (lastFetchedLangRef.current === null) {
      lastFetchedLangRef.current = dataRetrievedLanguage || locale;
    }

    if (lastFetchedLangRef.current === locale) return;

    lastFetchedLangRef.current = locale;
    const requestId = ++requestIdRef.current;
    updateHomeDataAndOperatorDetails(buildHomeUrlForLocale(locale, country, source), requestId);
  }, [language.locale, homeData?.country, source]);
};
